import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Grid, Typography, Button, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Alert, LinearProgress,
  Tooltip, Collapse, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Menu, CircularProgress
} from '@mui/material';
import {
  Add, Assignment, AutoFixHigh, Factory, Schedule,
  Warning, Refresh, ExpandMore,
  KeyboardArrowRight, Search,
  Person, Clear
} from '@mui/icons-material';
import { manufacturingAPI } from '../../services/api';
import { toast } from 'react-toastify';
import usePageTitle from '../../hooks/usePageTitle';

// ─── Debounce Hook ───────────────────────────────────────────────────
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

// ─── Date helpers ────────────────────────────────────────────────────
function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function formatDateShort(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function getDateKey(dateStr) {
  if (!dateStr) return 'no_due_date';
  const d = new Date(dateStr);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isOverdue(dateStr, status) {
  if (!dateStr) return false;
  if (status === 'completed' || status === 'cancelled') return false;
  const due = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
}

function isToday(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const today = new Date();
  return d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
}

function formatParam(date) {
  if (!date) return undefined;
  return date; // already YYYY-MM-DD from input type="date"
}

// ─── Status/Priority helpers ─────────────────────────────────────────
const STATUS_COLORS = {
  planned: 'info',
  in_progress: 'primary',
  completed: 'success',
  cancelled: 'error'
};

const STATUS_LABELS = {
  planned: 'Planned',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled'
};

const PRIORITY_COLORS = { 1: 'error', 2: 'error', 3: 'warning', 4: 'default' };
const PRIORITY_LABELS = { 1: 'Urgent', 2: 'High', 3: 'Normal', 4: 'Low' };

// ─── Group work orders by date ───────────────────────────────────────
function groupWorkOrders(workOrders) {
  const overdue = [];
  const byDate = {};
  const noDueDate = [];

  workOrders.forEach(wo => {
    if (!wo.due_date) {
      noDueDate.push(wo);
    } else if (isOverdue(wo.due_date, wo.status)) {
      overdue.push(wo);
    } else {
      const key = getDateKey(wo.due_date);
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(wo);
    }
  });

  // Sort date keys chronologically
  const sortedDateKeys = Object.keys(byDate).sort();

  const groups = [];

  if (overdue.length > 0) {
    groups.push({ key: 'overdue', label: 'OVERDUE', orders: overdue, type: 'overdue' });
  }

  sortedDateKeys.forEach(key => {
    const sampleDate = byDate[key][0].due_date;
    const todayFlag = isToday(sampleDate);
    const label = todayFlag ? `Today - ${formatDate(sampleDate)}` : formatDate(sampleDate);
    groups.push({ key, label, orders: byDate[key], type: todayFlag ? 'today' : 'future' });
  });

  if (noDueDate.length > 0) {
    groups.push({ key: 'no_due_date', label: 'No Due Date', orders: noDueDate, type: 'no_due_date' });
  }

  return groups;
}

// ═════════════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════════════
export default function WorkOrderManagement() {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [operations, setOperations] = useState([]);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [operationFilter, setOperationFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const debouncedSearch = useDebounce(searchInput, 500);

  // Expanded rows & execution cache
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState(new Set(['no_due_date']));

  // Dialogs
  const [createDialog, setCreateDialog] = useState(false);
  const [bomExplosionDialog, setBomExplosionDialog] = useState(false);
  const [createMenuAnchor, setCreateMenuAnchor] = useState(null);

  usePageTitle('Work Orders');

  // Load operations for filter dropdown
  useEffect(() => {
    manufacturingAPI.operations.list().then(res => {
      setOperations(res.data.results || res.data);
    }).catch(() => {});
  }, []);

  // Load work orders when filters change
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter) params.status = statusFilter;
      if (operationFilter) params.operation = operationFilter;
      if (dateFrom) params.due_date_from = formatParam(dateFrom);
      if (dateTo) params.due_date_to = formatParam(dateTo);
      if (dateFrom || dateTo) params.include_no_due_date = 'true';

      const response = await manufacturingAPI.workOrders.list(params);
      setWorkOrders(response.data.results || response.data);
    } catch (error) {
      console.error('Error loading work orders:', error);
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, operationFilter, dateFrom, dateTo]);

  useEffect(() => { loadData(); }, [loadData]);

  // Compute groups
  const groups = useMemo(() => groupWorkOrders(workOrders), [workOrders]);

  // Summary stats
  const stats = useMemo(() => {
    const active = workOrders.filter(wo => ['planned', 'released', 'in_progress'].includes(wo.status)).length;
    const overdueCount = workOrders.filter(wo => isOverdue(wo.due_date, wo.status)).length;
    const completed = workOrders.filter(wo => wo.status === 'completed').length;
    return { total: workOrders.length, active, overdue: overdueCount, completed };
  }, [workOrders]);

  // Toggle group collapse
  const toggleGroup = (key) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // Toggle row expand
  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreateFromBOM = async (bomData) => {
    try {
      const response = await manufacturingAPI.workOrders.createFromBomExplosion({
        item_id: bomData.item_id,
        quantity: bomData.quantity,
        customer_order_ref: bomData.customer_ref,
        due_date: bomData.due_date
      });
      if (response.data.work_orders.length > 0) {
        toast.success(`Created ${response.data.work_orders.length} work orders`);
      } else {
        toast.info(response.data.message);
      }
      setBomExplosionDialog(false);
      await loadData();
    } catch (error) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to create work orders from BOM');
      }
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setStatusFilter('');
    setOperationFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = searchInput || statusFilter || operationFilter || dateFrom || dateTo;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      {/* ─── Header ─── */}
      <Box className="page-header-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Factory color="primary" />
            Work Orders
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData} size="small">
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={(e) => setCreateMenuAnchor(e.currentTarget)}
            size="small"
          >
            Create
          </Button>
          <Menu
            anchorEl={createMenuAnchor}
            open={Boolean(createMenuAnchor)}
            onClose={() => setCreateMenuAnchor(null)}
          >
            <MenuItem onClick={() => { setCreateMenuAnchor(null); setBomExplosionDialog(true); }}>
              <AutoFixHigh sx={{ mr: 1, fontSize: 20 }} /> From Customer Order
            </MenuItem>
            <MenuItem onClick={() => { setCreateMenuAnchor(null); setCreateDialog(true); }}>
              <Add sx={{ mr: 1, fontSize: 20 }} /> Manual Work Order
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* ─── Filters ─── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search WO#, item, customer ref..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                <MenuItem value="">Active + Today's Completed</MenuItem>
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="planned">Planned</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Operation</InputLabel>
              <Select value={operationFilter} onChange={(e) => setOperationFilter(e.target.value)} label="Operation">
                <MenuItem value="">All Operations</MenuItem>
                {operations.map(op => (
                  <MenuItem key={op.id} value={op.id}>
                    {op.operation_code} - {op.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Due From"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Due To"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {hasActiveFilters && (
            <Grid item xs="auto">
              <Tooltip title="Clear all filters">
                <IconButton size="small" onClick={clearFilters}>
                  <Clear />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* ─── Summary Strip ─── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip label={`${stats.total} total`} variant="outlined" size="small" />
        <Chip label={`${stats.active} active`} color="primary" variant="outlined" size="small" />
        {stats.overdue > 0 && (
          <Chip label={`${stats.overdue} overdue`} color="error" size="small" icon={<Warning />} />
        )}
        <Chip label={`${stats.completed} completed`} color="success" variant="outlined" size="small" />
      </Box>

      {/* ─── Loading ─── */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* ─── Date-Grouped Table ─── */}
      {!loading && workOrders.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Schedule sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Work Orders Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasActiveFilters ? 'Try adjusting your filters.' : 'Create work orders from customer orders or manually.'}
          </Typography>
        </Paper>
      ) : (
        groups.map(group => (
          <DateGroupSection
            key={group.key}
            group={group}
            collapsed={collapsedGroups.has(group.key)}
            onToggleGroup={() => toggleGroup(group.key)}
            expandedRows={expandedRows}
            onToggleRow={toggleRow}
          />
        ))
      )}

      {/* ─── Dialogs ─── */}
      <CustomerOrderDialog
        open={bomExplosionDialog}
        onClose={() => setBomExplosionDialog(false)}
        onSubmit={handleCreateFromBOM}
      />
      <CreateWorkOrderDialog
        open={createDialog}
        onClose={() => setCreateDialog(false)}
        onSuccess={() => { setCreateDialog(false); loadData(); }}
      />
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Date Group Section
// ═════════════════════════════════════════════════════════════════════
function DateGroupSection({ group, collapsed, onToggleGroup, expandedRows, onToggleRow }) {
  const bgColor = group.type === 'overdue' ? 'error.main'
    : group.type === 'today' ? 'primary.main'
    : group.type === 'no_due_date' ? 'grey.600'
    : 'grey.700';

  const textColor = 'white';

  return (
    <Paper sx={{ mb: 1.5, overflow: 'hidden' }}>
      {/* Group Header */}
      <Box
        onClick={onToggleGroup}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          bgcolor: bgColor, color: textColor, px: 2, py: 1, cursor: 'pointer',
          '&:hover': { opacity: 0.9 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {collapsed ? <KeyboardArrowRight /> : <ExpandMore />}
          <Typography variant="subtitle1" fontWeight="bold">
            {group.label}
          </Typography>
          <Chip
            label={`${group.orders.length} order${group.orders.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 'bold' }}
          />
        </Box>
        {group.type === 'overdue' && <Warning />}
      </Box>

      {/* Group Content */}
      <Collapse in={!collapsed}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40 }} />
                <TableCell sx={{ fontWeight: 'bold' }}>WO #</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Operation</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Progress</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer Ref</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Due</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.orders.map(wo => (
                <WorkOrderRow
                  key={wo.id}
                  wo={wo}
                  expanded={expandedRows.has(wo.id)}
                  onToggle={() => onToggleRow(wo.id)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Work Order Row (with expandable detail)
// ═════════════════════════════════════════════════════════════════════
function WorkOrderRow({ wo, expanded, onToggle }) {
  const progressPct = wo.progress_percentage || 0;
  const qtyCompleted = parseFloat(wo.quantity_completed) || 0;
  const qtyTotal = parseFloat(wo.quantity_to_produce) || 0;

  return (
    <>
      <TableRow
        hover
        onClick={onToggle}
        sx={{ cursor: 'pointer', '& > *': { borderBottom: expanded ? 'none' : undefined } }}
      >
        <TableCell>
          <IconButton size="small">
            {expanded ? <ExpandMore fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="bold">{wo.work_order_number}</Typography>
          {wo.is_multi_step && (
            <Typography variant="caption" color="text.secondary">{wo.step_display}</Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight="bold" color="primary.main">
            {wo.output_item_display}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {wo.output_item_name}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{wo.operation_display}</Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2" fontWeight="bold">
            {qtyCompleted > 0 ? `${qtyCompleted}/${qtyTotal}` : qtyTotal}
          </Typography>
        </TableCell>
        <TableCell align="center" sx={{ minWidth: 110 }}>
          {qtyTotal > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(progressPct, 100)}
                sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                color={progressPct >= 100 ? 'success' : 'primary'}
              />
              <Typography variant="caption" sx={{ minWidth: 32 }}>
                {Math.round(progressPct)}%
              </Typography>
            </Box>
          )}
        </TableCell>
        <TableCell>
          <Chip
            label={STATUS_LABELS[wo.status] || wo.status}
            color={STATUS_COLORS[wo.status] || 'default'}
            size="small"
          />
        </TableCell>
        <TableCell>
          <Chip
            label={PRIORITY_LABELS[wo.priority] || `P${wo.priority}`}
            color={PRIORITY_COLORS[wo.priority] || 'default'}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {wo.customer_order_ref || '-'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color={isOverdue(wo.due_date, wo.status) ? 'error.main' : 'text.primary'}>
            {formatDateShort(wo.due_date)}
          </Typography>
        </TableCell>
      </TableRow>

      {/* Expanded Detail */}
      <TableRow>
        <TableCell colSpan={10} sx={{ py: 0, px: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <ExpandedWorkOrderDetail wo={wo} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Expanded Detail - Execution History
// ═════════════════════════════════════════════════════════════════════
function ExpandedWorkOrderDetail({ wo }) {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    manufacturingAPI.workOrderExecutions.list({ work_order: wo.id })
      .then(res => {
        if (!cancelled) setExecutions(res.data.results || res.data);
      })
      .catch(() => {
        if (!cancelled) setExecutions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [wo.id]);

  return (
    <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50' }}>
      <Grid container spacing={3}>
        {/* Left: Work Order Info */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Work Order Details
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <DetailRow label="Created" value={formatDate(wo.created_at)} />
            <DetailRow label="Created By" value={wo.created_by_name || '-'} />
            <DetailRow label="Scheduled Start" value={formatDate(wo.scheduled_start)} />
            <DetailRow label="Scheduled End" value={formatDate(wo.scheduled_end)} />
            {wo.actual_start && <DetailRow label="Actual Start" value={`${formatDate(wo.actual_start)} ${formatTime(wo.actual_start)}`} />}
            {wo.actual_end && <DetailRow label="Actual End" value={`${formatDate(wo.actual_end)} ${formatTime(wo.actual_end)}`} />}
            {wo.notes && <DetailRow label="Notes" value={wo.notes} />}
          </Box>

          {/* Linked Customer Orders */}
          {wo.linked_orders_display && wo.linked_orders_display.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Linked Customer Orders
              </Typography>
              {wo.linked_orders_display.map((order, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                  <Chip label={order.order_number} size="small" variant="outlined" />
                  <Typography variant="caption" color="text.secondary">
                    {order.customer_name} {order.due_date ? `- Due ${formatDateShort(order.due_date)}` : ''}
                    {order.quantity_ordered != null ? ` (${order.quantity_ordered} ordered)` : ''}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Grid>

        {/* Right: Execution History */}
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Person fontSize="small" />
            Execution History
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : executions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No execution records yet.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Who</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }} align="right">Qty Done</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }} align="right">Scrapped</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>When</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Terminal</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {executions.map(exec => (
                    <TableRow key={exec.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {exec.employee?.user_name || exec.employee_display || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          {parseFloat(exec.quantity_completed)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(exec.quantity_scrapped) > 0 ? (
                          <Typography variant="body2" color="error.main">
                            {parseFloat(exec.quantity_scrapped)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">0</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(exec.start_time)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(exec.start_time)}
                          {exec.end_time && exec.end_time !== exec.start_time ? ` - ${formatTime(exec.end_time)}` : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {exec.duration_minutes != null ? `${Math.round(exec.duration_minutes)} min` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{exec.computer_used || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ maxWidth: 150, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {exec.notes || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

// Small helper for detail rows
function DetailRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>{label}:</Typography>
      <Typography variant="caption">{value}</Typography>
    </Box>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Customer Order Dialog (BOM Explosion)
// ═════════════════════════════════════════════════════════════════════
function CustomerOrderDialog({ open, onClose, onSubmit }) {
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [customerRef, setCustomerRef] = useState('');
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    if (open) {
      manufacturingAPI.items.list({ type: 'finished_good' }).then(res => {
        setItems(res.data.results || res.data);
      }).catch(() => {});
    }
  }, [open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      item_id: selectedItemId,
      quantity: parseInt(quantity),
      customer_ref: customerRef,
      due_date: dueDate || null
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment color="primary" />
            Create Work Orders from Customer Order
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will analyze stock and create work orders only for items that are short.
          </Alert>

          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Product</InputLabel>
            <Select
              value={selectedItemId}
              onChange={(e) => setSelectedItemId(e.target.value)}
              label="Product"
              required
            >
              {items.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.item_code} - {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth label="Quantity" type="number" value={quantity}
            onChange={(e) => setQuantity(e.target.value)} required sx={{ mb: 2 }}
            inputProps={{ min: 1 }}
          />

          <TextField
            fullWidth label="Customer Order Reference" value={customerRef}
            onChange={(e) => setCustomerRef(e.target.value)} sx={{ mb: 2 }}
            placeholder="e.g., SO-001234"
          />

          <TextField
            fullWidth label="Due Date" type="datetime-local" value={dueDate}
            onChange={(e) => setDueDate(e.target.value)} sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">Check Stock & Create</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Manual Create Work Order Dialog
// ═════════════════════════════════════════════════════════════════════
function CreateWorkOrderDialog({ open, onClose, onSuccess }) {
  const [items, setItems] = useState([]);
  const [operations, setOperations] = useState([]);
  const [formData, setFormData] = useState({
    output_item_id: '', operation_id: '', quantity_to_produce: '',
    priority: 3, customer_order_ref: '', due_date: '', notes: ''
  });

  useEffect(() => {
    if (open) {
      Promise.all([
        manufacturingAPI.items.list(),
        manufacturingAPI.operations.list()
      ]).then(([itemsRes, opsRes]) => {
        setItems(itemsRes.data.results || itemsRes.data);
        setOperations(opsRes.data.results || opsRes.data);
      }).catch(() => {});
    }
  }, [open]);

  const handleItemChange = (itemId) => {
    if (itemId && operations.length > 0) {
      setFormData(prev => ({ ...prev, output_item_id: itemId, operation_id: operations[0].id }));
    } else {
      setFormData(prev => ({ ...prev, output_item_id: itemId }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      output_item: formData.output_item_id,
      operation: formData.operation_id,
      quantity_to_produce: parseInt(formData.quantity_to_produce),
      priority: formData.priority,
      customer_order_ref: formData.customer_order_ref,
      due_date: formData.due_date || null,
      notes: formData.notes,
      scheduled_start: new Date().toISOString(),
      scheduled_end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    };

    try {
      await manufacturingAPI.workOrders.create(payload);
      toast.success('Work order created successfully');
      onSuccess();
      setFormData({
        output_item_id: '', operation_id: '', quantity_to_produce: '',
        priority: 3, customer_order_ref: '', due_date: '', notes: ''
      });
    } catch (error) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else if (error.response?.data) {
        const errors = Object.entries(error.response.data)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('\n');
        toast.error(`Validation errors:\n${errors}`);
      } else {
        toast.error('Failed to create work order');
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Add color="primary" />
            Create Manual Work Order
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            For customer orders, use "Create from Customer Order" instead.
          </Alert>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>What to Make *</InputLabel>
                <Select
                  value={formData.output_item_id}
                  onChange={(e) => handleItemChange(e.target.value)}
                  label="What to Make *"
                  required
                >
                  {items.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.item_code} - {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Operation *</InputLabel>
                <Select
                  value={formData.operation_id}
                  onChange={(e) => setFormData({ ...formData, operation_id: e.target.value })}
                  label="Operation *"
                  required
                >
                  {operations.map((op) => (
                    <MenuItem key={op.id} value={op.id}>
                      {op.operation_code} - {op.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Quantity *" type="number"
                value={formData.quantity_to_produce}
                onChange={(e) => setFormData({ ...formData, quantity_to_produce: e.target.value })}
                required inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value={1}>1 - Urgent</MenuItem>
                  <MenuItem value={2}>2 - High</MenuItem>
                  <MenuItem value={3}>3 - Normal</MenuItem>
                  <MenuItem value={4}>4 - Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Customer Order Reference"
                value={formData.customer_order_ref}
                onChange={(e) => setFormData({ ...formData, customer_order_ref: e.target.value })}
                placeholder="e.g., SO-001234"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth label="Due Date" type="datetime-local"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth label="Notes" multiline rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Special instructions..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">Create Work Order</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
